import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

const MainEmail = ({
  emails,
  activeCategory,
  loading: externalLoading,
  error: externalError,
  emailsPerPage = 10,
  setActiveCategory,
  onEmailSelect,
  onCategoryChange,
  onFetchEmails,
  onDelete,
  email,
  viewingAllAccounts = true,
}) => {
  const [deletingId, setDeletingId] = useState(null);
  // Add a ref to cache emails during loading
  const cachedEmails = useRef({});
  const initialLoadDone = useRef(false);
  // Add internal loading and error states
  const [internalLoading, setLoading] = useState(false);
  const [internalError, setError] = useState(null);

  // Combine external and internal states
  const loading = externalLoading || internalLoading;
  const error = externalError || internalError;

  // Add a dedicated force update counter for email changes
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);

  // Load emails from localStorage on mount if available
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;

      try {
        // Check if emails prop is empty or loading
        const shouldLoadFromCache =
          !emails ||
          (!emails.needs_response?.length &&
            !emails.spam?.length &&
            !emails.notify?.length);

        if (shouldLoadFromCache) {
          const cachedEmailsData = localStorage.getItem("cached_emails");
          if (cachedEmailsData) {
            const parsedEmails = JSON.parse(cachedEmailsData);

            // Check if we have any emails in the cache
            if (
              parsedEmails &&
              (parsedEmails.needs_response?.length > 0 ||
                parsedEmails.spam?.length > 0 ||
                parsedEmails.notify?.length > 0)
            ) {
              // Update cachedEmails ref
              cachedEmails.current = parsedEmails;
              console.log("MainEmail: Loaded emails from localStorage");
            }
          }
        }
      } catch (error) {
        console.error("Error loading emails from localStorage:", error);
      }
    }
  }, [emails]);

  // Update cached emails whenever new non-empty emails arrive
  useEffect(() => {
    if (!externalLoading && emails) {
      // Only update cache if we have actual email data in at least one category
      const hasAnyData =
        emails.needs_response?.length > 0 ||
        emails.spam?.length > 0 ||
        emails.notify?.length > 0;

      if (hasAnyData) {
        // Start with current cached emails to preserve existing data
        const newCache = { ...cachedEmails.current };

        // Only update categories that have data in the response
        if (
          Array.isArray(emails.needs_response) &&
          emails.needs_response.length > 0
        ) {
          newCache.needs_response = [...emails.needs_response];
        }

        if (Array.isArray(emails.spam) && emails.spam.length > 0) {
          newCache.spam = [...emails.spam];
        }

        if (Array.isArray(emails.notify) && emails.notify.length > 0) {
          newCache.notify = [...emails.notify];
        }

        if (emails.pagination) {
          // Ensure pagination counts reflect our merged data
          const mergedPagination = {
            ...emails.pagination,
            category_counts: {
              needs_response: newCache.needs_response?.length || 0,
              spam: newCache.spam?.length || 0,
              notify: newCache.notify?.length || 0,
            },
          };

          // Update total count based on our merged data
          mergedPagination.total_count =
            mergedPagination.category_counts.needs_response +
            mergedPagination.category_counts.spam +
            mergedPagination.category_counts.notify;

          newCache.pagination = mergedPagination;
        }

        cachedEmails.current = newCache;

        // Force a re-render to show the updated emails immediately
        setForceUpdateCounter((prev) => prev + 1);
      }
    }
  }, [emails, externalLoading]);

  // Get the emails to display (using cached data when needed)
  const displayEmails = useMemo(() => {
    // Create a combined emails object with default structure
    const combinedEmails = {
      needs_response: [],
      spam: [],
      notify: [],
      pagination: {
        total_count: 0,
        total_pages: 0,
        has_more: false,
        category_counts: {
          needs_response: 0,
          spam: 0,
          notify: 0,
        },
      },
    };

    // First, use emails from props if they exist and have data
    if (
      emails &&
      (emails.needs_response?.length > 0 ||
        emails.spam?.length > 0 ||
        emails.notify?.length > 0)
    ) {
      if (emails.needs_response?.length > 0) {
        combinedEmails.needs_response = emails.needs_response;
      }
      if (emails.spam?.length > 0) {
        combinedEmails.spam = emails.spam;
      }
      if (emails.notify?.length > 0) {
        combinedEmails.notify = emails.notify;
      }
      if (emails.pagination) {
        combinedEmails.pagination = { ...emails.pagination };
      }

      return combinedEmails;
    }

    // If emails prop is empty, use cached data
    if (cachedEmails.current.needs_response?.length > 0) {
      combinedEmails.needs_response = cachedEmails.current.needs_response;
    }
    if (cachedEmails.current.spam?.length > 0) {
      combinedEmails.spam = cachedEmails.current.spam;
    }
    if (cachedEmails.current.notify?.length > 0) {
      combinedEmails.notify = cachedEmails.current.notify;
    }
    if (cachedEmails.current.pagination) {
      combinedEmails.pagination = cachedEmails.current.pagination;
    }

    return combinedEmails;
  }, [emails, forceUpdateCounter]);

  const formatEmailTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "";
    }
  };

  const getSenderName = (emailData) => {
    if (!emailData || !emailData.email) return "Unknown";

    if (emailData.email.display_name) return emailData.email.display_name;

    if (emailData.email.from_name) return emailData.email.from_name;

    if (emailData.email.from_email) {
      const emailParts = emailData.email.from_email.split("@");
      return emailParts[0] || "Unknown";
    }

    return "Unknown";
  };

  // Get the account information from an email if available
  const getAccountInfo = (emailData) => {
    if (viewingAllAccounts && emailData && emailData.account) {
      return {
        id: emailData.account.id,
        email: emailData.account.email,
        provider: emailData.account.provider,
      };
    }
    return null;
  };

  // Format account display for UI
  const formatAccountDisplay = (account) => {
    if (!account || !account.email) return "";

    // Get just the local part of the email (before the @)
    const localPart = account.email.split("@")[0];

    // Return based on provider
    if (account.provider === "google") {
      return (
        <span className="flex items-center">
          <img
            src="https://www.google.com/favicon.ico"
            alt="Google"
            className="w-3 h-3"
          />
          {localPart}
        </span>
      );
    } else if (account.provider === "outlook") {
      return (
        <span className="flex items-center">
          <img
            src="https://www.microsoft.com/favicon.ico"
            alt="Outlook"
            className="w-3 h-3"
          />
          {localPart}
        </span>
      );
    } else {
      return (
        <span className="flex items-center">
          <svg
            className="w-3 h-3 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          {localPart}
        </span>
      );
    }
  };

  // Use the emails directly from the current category
  // const currentEmails = useMemo(() => {
  //   const categoryEmails = displayEmails[activeCategory] || [];
  //   if (!Array.isArray(categoryEmails)) return [];

  //   return categoryEmails;
  // }, [displayEmails, activeCategory]);
  const currentEmails = useMemo(() => {
    const categoryEmails = displayEmails[activeCategory] || [];
    if (!Array.isArray(categoryEmails)) return [];

    // Sort emails: VIP emails first, then by date (newest first)
    return categoryEmails.sort((a, b) => {
      // First sort by VIP status (VIP emails come first)
      if (a.email?.is_vip !== b.email?.is_vip) {
        return b.email?.is_vip - a.email?.is_vip; // true (1) comes before false (0)
      }

      // Then sort by date (newest first) for emails with the same VIP status
      const dateA = new Date(a.email?.send_time || a.email?.date || 0);
      const dateB = new Date(b.email?.send_time || b.email?.date || 0);
      return dateB - dateA;
    });
  }, [displayEmails, activeCategory]);

  const categoryTotalCounts = useMemo(() => {
    // Use server-provided counts if available
    if (displayEmails.pagination?.category_counts) {
      return {
        needs_response:
          displayEmails.pagination.category_counts.needs_response || 0,
        spam: displayEmails.pagination.category_counts.spam || 0,
        notify: displayEmails.pagination.category_counts.notify || 0,
      };
    }

    // Fallback to length of arrays
    return {
      needs_response: displayEmails.needs_response?.length || 0,
      spam: displayEmails.spam?.length || 0,
      notify: displayEmails.notify?.length || 0,
    };
  }, [displayEmails]);

  // Handle refresh button click - simplified to just call the provided onFetchEmails function
  const handleRefresh = useCallback(() => {
    if (onFetchEmails) {
      setLoading(true);
      try {
        Promise.resolve(onFetchEmails(true)).finally(() => {
          setLoading(false);
        });
      } catch (err) {
        console.error("Error refreshing emails:", err);
        setLoading(false);
      }
    }
  }, [onFetchEmails]);

  // Add a useEffect to update the component when emails change
  useEffect(() => {
    if (emails) {
      // Force re-render by updating the force update counter
      setForceUpdateCounter((prev) => prev + 1);
    }
  }, [emails]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1">
        <div className="p-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FCFCFC] px-4 shadow-sm sticky top-0 z-10 rounded-t-lg">
              <div className="border-b border-gray-200">
                <nav className="flex items-center justify-between">
                  <div className="flex space-x-4 flex-grow mr-4">
                    <button
                      onClick={() => onCategoryChange("needs_response")}
                      className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                        activeCategory === "needs_response"
                          ? "border-blue-500 text-blue-500"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 mr-1 text-[#1F9BFF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        Respond ({categoryTotalCounts.needs_response})
                      </div>
                    </button>
                    <button
                      onClick={() => onCategoryChange("notify")}
                      className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                        activeCategory === "notify"
                          ? "border-blue-500 text-blue-500"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 mr-1 text-[#1F9BFF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                        </svg>
                        Inform ({categoryTotalCounts.notify})
                      </div>
                    </button>
                    <button
                      onClick={() => onCategoryChange("spam")}
                      className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                        activeCategory === "spam"
                          ? "border-blue-500 text-blue-500"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 mr-1 text-[#1F9BFF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        Unimportant ({categoryTotalCounts.spam})
                      </div>
                    </button>
                  </div>

                  {/* <div className="flex-shrink-0">
                    <button
                      className="p-2 text-gray-500 hover:text-blue-600 transition-colors duration-200"
                      onClick={handleRefresh}
                      disabled={loading}
                      type="button"
                    >
                      {loading ? (
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Refresh Emails</span>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            ></path>
                          </svg>
                        </div>
                      )}
                    </button>
                  </div> */}
                </nav>
              </div>
            </div>
            <div className="bg-[#FCFCFC] shadow min-h-[15rem] h-[calc(100vh-16rem)] overflow-y-auto flex flex-col">
              <div className="divide-y divide-gray-200 flex-1 flex flex-col">
                {loading && currentEmails.length === 0 ? (
                  <div className="p-4 text-center flex flex-col items-center justify-center flex-1 space-y-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                    <div className="text-gray-500">
                      Loading emails
                      {activeCategory
                        ? ` in ${activeCategory.replace("_", " ")}`
                        : ""}
                      ...
                    </div>
                    <div className="text-xs text-gray-400">
                      {viewingAllAccounts
                        ? "Fetching from all connected accounts"
                        : "Fetching from current account"}
                    </div>
                  </div>
                ) : error ? (
                  <div className="p-4 text-center text-red-500 flex items-center justify-center flex-1">
                    {error}
                  </div>
                ) : currentEmails.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 flex items-center justify-center flex-1">
                    No emails in this category
                  </div>
                ) : (
                  // Email list - will show even during loading if we have emails
                  currentEmails.map((emailData) => (
                    <div
                      key={emailData.email.id}
                      onClick={() => onEmailSelect(emailData)}
                      className="h-10 py-2 px-2 hover:bg-white hover:shadow-lg cursor-pointer group transition-colors duration-200 ease-in-out overflow-hidden"
                    >
                      <div className="flex items-center justify-between h-full w-full">
                        <div className="flex items-center flex-grow min-w-0">
                          <input
                            type="checkbox"
                            className="mr-3 h-4 w-4 text-blue-600 rounded transition-colors duration-200"
                            onClick={(e) => e.stopPropagation()}
                          />

                          <button
                            className="mr-3 text-gray-400 hover:text-yellow-500 transition-colors duration-200 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </button>

                          <div
                            className="min-w-0 flex-1 flex items-center overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-sm font-medium mr-2 truncate max-w-[150px] flex-shrink-0">
                              {getSenderName(emailData)}
                            </span>
                            <span className="text-sm text-gray-600 truncate">
                              {emailData.email.subject || "No subject"}
                            </span>

                            {viewingAllAccounts && emailData.account && (
                              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                                {formatAccountDisplay(emailData.account)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span className="text-xs text-gray-500 w-20 text-right group-hover:hidden transition-opacity duration-200">
                            {(() => {
                              try {
                                const now = new Date();
                                const sendTime = new Date(
                                  emailData.email.send_time
                                );
                                if (isNaN(sendTime.getTime())) return "";

                                const diffInSeconds = Math.floor(
                                  (now - sendTime) / 1000
                                );

                                if (diffInSeconds < 60) return "just now";
                                if (diffInSeconds < 3600)
                                  return `${Math.floor(
                                    diffInSeconds / 60
                                  )}m ago`;
                                if (diffInSeconds < 86400)
                                  return `${Math.floor(
                                    diffInSeconds / 3600
                                  )}h ago`;
                                if (diffInSeconds < 604800)
                                  return `${Math.floor(
                                    diffInSeconds / 86400
                                  )}d ago`;
                                if (diffInSeconds < 2592000)
                                  return `${Math.floor(
                                    diffInSeconds / 604800
                                  )}w ago`;
                                if (diffInSeconds < 31536000)
                                  return `${Math.floor(
                                    diffInSeconds / 2592000
                                  )}mo ago`;
                                return `${Math.floor(
                                  diffInSeconds / 31536000
                                )}y ago`;
                              } catch (e) {
                                return "";
                              }
                            })()}
                          </span>

                          <div className="hidden group-hover:flex space-x-1 transition-all duration-200 ease-in-out">
                            <span className="px-2 py-1 bg-blue-100 rounded text-sm text-blue-800">
                              From: {emailData?.email?.to_email}
                            </span>
                          </div>

                          <div
                            className="hidden group-hover:flex space-x-1 transition-all duration-200 ease-in-out"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="p-1 hover:bg-blue-100 rounded transition-colors duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEmailSelect(emailData);
                              }}
                            >
                              <span className="px-2 py-1 bg-blue-100 rounded text-sm text-blue-800">
                                Preview
                              </span>
                            </button>
                            <button
                              className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg
                                className="h-4 w-4 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                                />
                              </svg>
                            </button>
                            <button
                              className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                const emailId = emailData.email.id;
                                setDeletingId(emailId);
                                onDelete(emailId).finally(() =>
                                  setDeletingId(null)
                                );
                              }}
                              disabled={deletingId === emailData.email.id}
                            >
                              {deletingId === emailData.email.id ? (
                                <svg
                                  className="animate-spin h-4 w-4 text-gray-500"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                              ) : (
                                <svg
                                  className="h-4 w-4 text-gray-500"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Show a loading indicator when refreshing */}
                {loading && currentEmails.length > 0 && (
                  <div className="py-2 px-4 text-center">
                    <span className="inline-block animate-pulse bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs">
                      Refreshing emails...
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with info (replaced pagination) */}
            <div className="bg-white shadow-sm py-3 px-4 rounded-b-lg flex justify-between items-center">
              <div className="text-xs text-gray-500">
                {currentEmails.length > 0
                  ? `Showing ${currentEmails.length} email${
                      currentEmails.length !== 1 ? "s" : ""
                    }`
                  : "No emails to display"}
              </div>

              <div className="text-xs text-gray-500">
                Last updated:{" "}
                {localStorage.getItem("emails_last_fetch")
                  ? new Date(
                      localStorage.getItem("emails_last_fetch")
                    ).toLocaleTimeString()
                  : "Never"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainEmail;
